/**
 * Thin wrapper over the generated resolve mutation, plus the one thing the
 * generated client gets wrong at runtime.
 *
 * The declared error union (`{status: 400, payload}`) never occurs: the
 * post-processed fetcher throws `ApiError`, which names the same body `body`.
 * This module is the only place in the feature allowed to know that, exactly
 * as `parseLinkResult` confines it in `SalesforceAccounts`.
 *
 * Do not route this through `Licenses/lib/mutationHandlers.ts` — it reads
 * `error.payload` and would silently discard the body, taking `detail` and
 * `correlationId` with it. Convergence is APP-2478.
 *
 * `reason` is audited operator narrative: request body only, never a query
 * key, a URL, a log line or a toast.
 */
import { ApiError } from '@/api/ApiError'
import type { ProblemDetailResponse } from '@/generated/http-clients/v3ApiSchemas'

/** What the form renders in its inline alert. */
export type ResolveIngestError = {
  status: number
  /** `ProblemDetailResponse.detail`, absent when the body was not one. */
  detail?: string
  /** `ProblemDetailResponse.correlationId`, for a bug report. */
  correlationId?: string
}

function isProblemDetail(body: unknown): body is ProblemDetailResponse {
  return typeof body === 'object' && body !== null && !Array.isArray(body)
}

/**
 * Normalises whatever the mutation threw into the three fields the alert
 * shows.
 *
 * `ApiError.fromResponse` falls back to the raw response text when the body
 * is not JSON, so `body` can be a string. That is diagnostic, not display
 * copy: it is dropped and the caller falls back to its generic message rather
 * than putting a stack trace or an HTML error page in front of an operator.
 *
 * A `NetworkError`, or anything else, has no status. It reports as `0`, which
 * the caller treats as "no response" rather than as an HTTP code.
 */
export function toResolveIngestError(error: unknown): ResolveIngestError {
  if (!(error instanceof ApiError)) {
    return { status: 0 }
  }

  if (!isProblemDetail(error.body)) {
    return { status: error.status }
  }

  return {
    status: error.status,
    detail: error.body.detail,
    correlationId: error.body.correlationId,
  }
}
